import * as ENVIRONMENTS from 'constants/environments'
import * as ROUTES from 'constants/routes'
import { env } from 'utils/env'
import { styled, useTheme } from '@mui/material'
import AppBar from '@mui/material/AppBar'
import Button from '@mui/material/Button'
import Toolbar from '@mui/material/Toolbar'
import { useRouteMatch } from 'react-router-dom'
import { ShareButton } from 'components/cell-visualizations/topBar/ShareButton'
import DownloadIndicator from 'components/shared/DownloadIndicator'
import { DownloadItem } from 'components/shared/DownloadPanel'
import useDownloadsSlice from 'redux/slices/hooks/useDownloadsSlice'
import useFileDownload from 'utils/useFileDownload'
import { useAuth } from '../../services/auth'
import NavigationTopBarTitle from './NavigationTopBarTitle'
import { TopNavMenu } from './TopNavMenu'

const NavAppBar = styled(AppBar)(({ theme }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  boxShadow: '0',
  zIndex: theme.zIndex.drawer + 1,
}))

const NavTopBarLoginButton = styled(Button)({
  color: 'white',
})

const ProfilePicture = styled('img')({
  width: '30px',
  height: '30px',
  borderRadius: '15px',
})

export const NavigationTopBar = (): JSX.Element => {
  const theme = useTheme()
  const isExperimentPlanning = useRouteMatch(ROUTES.EXPERIMENT_PLANNING)

  const { user, isAuthenticated, supportsLogin, loginWithRedirect, logout } = useAuth()

  // Enhanced download functionality
  const {
    downloads,
    updateDownloadItem,
    removeDownloadItem,
    autoDownload,
    setAutoDownloadEnabled,
  } = useDownloadsSlice()
  const { downloadFile } = useFileDownload()

  const onDownload = async (item: DownloadItem, fileName: string) => {
    // Only allow download if the item is ready with a valid downloadUrl
    if (!item.downloadUrl || item.status !== 'ready') return

    // Update status to downloading
    updateDownloadItem(item.id, { status: 'downloading' })

    try {
      await downloadFile(item.downloadUrl, fileName, {
        onSuccess: () => {
          updateDownloadItem(item.id, { status: 'completed' })
        },
        onError: (error) => {
          console.error('Download failed:', error)
          updateDownloadItem(item.id, {
            status: 'error',
            errorMessage: error.message,
          })
        },
      })
    } catch (error) {
      console.error('Download failed:', error)
      updateDownloadItem(item.id, {
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Download failed',
      })
    }
  }

  const onCancel = (itemId: string) => {
    removeDownloadItem(itemId)
  }

  const onDismiss = (itemId: string) => {
    removeDownloadItem(itemId)
  }

  const onFileNameChange = (itemId: string, fileName: string) => {
    updateDownloadItem(itemId, { fileName })
  }

  const onAutoDownloadToggle = (enabled: boolean) => {
    setAutoDownloadEnabled(enabled)
  }

  let appTitle: string
  let backgroundColor: string

  if (env.APP_ENV === ENVIRONMENTS.PRODUCTION) {
    appTitle = 'Axon Data Suite'
    backgroundColor = theme.palette.text.primary
  } else if (env.APP_ENV === ENVIRONMENTS.STAGING) {
    appTitle = 'Axon Data Suite'
    backgroundColor = theme.palette.info.main
  } else if (env.APP_ENV === ENVIRONMENTS.EXTERNAL_DEMO) {
    appTitle = 'Axon Data Suite'
    backgroundColor = theme.palette.text.primary
  } else {
    let suffix = 'Local'
    if (env.APP_ENV) {
      suffix = `${env.APP_ENV}`
    }
    appTitle = `Axon Data Suite - Development - ${suffix}`
    backgroundColor = theme.palette.info.main
  }

  function handleLogout() {
    logout({ returnTo: `${window.location.origin}` })
  }

  function handleLogin() {
    loginWithRedirect({
      returnTo: window.location.pathname,
    })
  }

  return (
    <div>
      <NavAppBar
        position="fixed"
        sx={{
          backgroundColor,
        }}
      >
        <Toolbar variant="dense">
          <TopNavMenu />
          <NavigationTopBarTitle appTitle={appTitle} />
          {isExperimentPlanning && (
            <div style={{ marginRight: '16px' }}>
              <ShareButton />
            </div>
          )}

          {/* Download Indicator - positioned before login/profile */}
          <div style={{ marginRight: '16px' }}>
            <DownloadIndicator
              downloads={downloads}
              onDownload={onDownload}
              onCancel={onCancel}
              onDismiss={onDismiss}
              onFileNameChange={onFileNameChange}
              autoDownload={autoDownload}
              onAutoDownloadToggle={onAutoDownloadToggle}
            />
          </div>

          {supportsLogin && (
            <>
              {isAuthenticated ? (
                <>
                  <NavTopBarLoginButton onClick={handleLogout}>Log out</NavTopBarLoginButton>
                  <ProfilePicture alt="user profile" src={(user?.picture as string) || undefined} />
                </>
              ) : (
                <NavTopBarLoginButton onClick={handleLogin}>Login</NavTopBarLoginButton>
              )}
            </>
          )}
        </Toolbar>
      </NavAppBar>
    </div>
  )
}

export default NavigationTopBar
